import React, { useEffect, useState } from 'react';
import { Star, Users, GitCommit, GitFork, Eye, Globe, Building, MapPin } from 'lucide-react';
import axios from 'axios';
import { Card } from '../components/ui/card';

interface GitHubContributor {
  login: string;
  avatar_url: string;
  github_url: string;
  contributions: number;
}

interface GitHubStats {
  id: string;
  stargazers_count: number;
  watchers_count: number;
  forks_count: number;
  total_commits_year: number;
  total_contributors: number;
  core_contributors: GitHubContributor[];
  description: string;
  last_github_update: string;
  last_fetched_at: string;
  owner_avatar_url: string;
  owner_bio: string;
  owner_company: string | null;
  owner_github_url: string;
  owner_location: string | null;
  owner_display_name: string;
  owner_name: string;
  owner_followers: number;
  repo_name: string;
}

const GitHubSection = ({ agentId }: { agentId?: string }) => {
  const [stats, setStats] = useState<GitHubStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldShow, setShouldShow] = useState(true);

  useEffect(() => {
    const fetchGitHubStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/agents/${agentId}/github`
        );
        
        // Only show section if we have meaningful data
        if (response.data && Object.keys(response.data).length > 0) {
          setStats(response.data);
          setShouldShow(true);
        } else {
          setShouldShow(false);
        }
      } catch (err) {
        console.error('Error fetching GitHub data:', err);
        setShouldShow(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (agentId) {
      fetchGitHubStats();
    } else {
      setShouldShow(false);
    }
  }, [agentId]);

  // Don't render anything if we shouldn't show the section
  if (!shouldShow || !stats) {
    return null;
  }

  // Don't show loading state, just return null until we have data
  if (isLoading) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="border-t border-gray-800 pt-6 mt-6">
      <h2 className="text-lg font-semibold mb-6">GitHub Details</h2>
      
      <div className="space-y-6">
        {/* Owner Info Card */}
        <Card className="bg-[#1A1721]/50 p-6">
          <div className="flex items-start gap-6">
            <a 
              href={stats.owner_github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <img 
                src={stats.owner_avatar_url} 
                alt={stats.owner_display_name}
                className="w-16 h-16 rounded-full hover:ring-2 hover:ring-blue-500 transition-all"
              />
            </a>
            <div className="flex-grow space-y-2">
              <div>
                <a 
                  href={stats.owner_github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xl font-medium hover:text-blue-400 transition-colors"
                >
                  {stats.owner_display_name}
                </a>
                <div className="text-sm text-gray-400">{stats.owner_bio}</div>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {stats.owner_followers.toLocaleString()} followers
                </div>
                {stats.owner_location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {stats.owner_location}
                  </div>
                )}
                {stats.owner_company && (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    {stats.owner_company}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Repository Stats Card */}
        <Card className="bg-[#1A1721]/50 p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-2">{stats.repo_name}</h3>
              <p className="text-gray-400">{stats.description}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-gray-400">Stars</span>
                </div>
                <span className="text-2xl font-medium">{stats.stargazers_count.toLocaleString()}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <GitFork className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-400">Forks</span>
                </div>
                <span className="text-2xl font-medium">{stats.forks_count.toLocaleString()}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-gray-400">Watchers</span>
                </div>
                <span className="text-2xl font-medium">{stats.watchers_count.toLocaleString()}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <GitCommit className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-400">Commits (Year)</span>
                </div>
                <span className="text-2xl font-medium">{stats.total_commits_year.toLocaleString()}</span>
              </div>
            </div>

            <div className="text-sm text-gray-400">
              <div>Last Updated: {formatDate(stats.last_github_update)}</div>
              <div>Last Fetched: {formatDate(stats.last_fetched_at)}</div>
            </div>
          </div>
        </Card>

        {/* Contributors Card */}
        <Card className="bg-[#1A1721]/50 p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Contributors</h3>
              <span className="text-gray-400">{stats.total_contributors.toLocaleString()} total</span>
            </div>

            <div className="divide-y divide-gray-800">
              {stats.core_contributors.map((contributor, index) => (
                <div key={contributor.login} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <a
                        href={contributor.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                      >
                        <img
                          src={contributor.avatar_url}
                          alt={contributor.login}
                          className="w-10 h-10 rounded-full hover:ring-2 hover:ring-blue-500 transition-all"
                        />
                      </a>
                      <div>
                        <a
                          href={contributor.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium hover:text-blue-400 transition-colors"
                        >
                          {contributor.login}
                        </a>
                        <div className="text-sm text-gray-400">
                          #{index + 1} Top Contributor
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{contributor.contributions.toLocaleString()}</div>
                      <div className="text-sm text-gray-400">commits</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GitHubSection;